<template>
  <div>
    <template
      v-if="!GET_IS_MANAGED_CAMPAIGN"
    >
      <self-service-template-selection
        @templateSelectionChanged="templateSelectionChanged"
        @isValidSelfServiceTemplateSelection="setIsValidSelfServiceTemplateSelection"
      />
    </template>
    <b-row
      v-else
    >
      <b-col
        class="mb-5"
        lg="6"
      >
        <b-form-group
          data-test="template-selector"
          invalid-feedback="Please select a template"
        >
          <template
            label-for="template-select"
            slot="label"
            class="form-label"
          >
            <span class="form-label">Template Group</span>
          </template>
          <b-form-input
            v-if="GET_IS_READ_ONLY('creative-properties')"
            v-model="selectedTemplateGroup.name"
            readonly
          />
          <BaseAutocomplete
            v-else
            id="creative-template-select"
            v-model="selectedTemplateGroup"
            type="dropdown"
            :items="availableTemplateGroups"
            data-test="creative-template-select"
            @change="updateCreativeTemplateGroup(selectedTemplateGroup)"
          />
        </b-form-group>
        <TemplateCollectionTable
          :group-selected="groupSelected"
          :selected-group-id="selectedGroupId"
          :group-id-changed="groupIdHasChanged"
          :is-new-campaign="GET_IS_NEW_CAMPAIGN"
          :template-list="templateList"
          :disabled="GET_IS_CAMPAIGN_READONLY"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  mapState, mapGetters, mapActions, mapMutations,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as contentTypes from '@/modules/content/store/types';
import * as templatesTypes from '@/modules/templates/store/types';

import suppressibleBox from '@/core/mixins/suppressibleBox';
import TemplateCollectionTable from '@/modules/templates/components/TemplateCollectionTable';
import SelfServiceTemplateSelection from '@/modules/templates/components/templateSelection/SelfServiceTemplateSelection';

export default {
  name: 'TemplateSelection',
  mixins: [
    suppressibleBox,
  ],
  components: {
    TemplateCollectionTable,
    SelfServiceTemplateSelection,
  },
  props: {
    templateGroup: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      availableTemplateGroups: [],
      selectedTemplateGroup: {},
      groupSelected: false,
      selectedGroupId: null,
      templateList: [],
      isValidSelfServiceTemplateSelection: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'dealOverrides',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_IS_READ_ONLY,
      campaignTypes.GET_IS_NEW_CAMPAIGN,
      campaignTypes.GET_IS_CAMPAIGN_STATE,
    ]),
    ...mapState('content', [
      'creative',
      'creatives',
      'activeCreatives',
    ]),
    ...mapState('templates', [
      'availableTemplates',
      'selectedTemplates',
    ]),
    groupIdHasChanged() {
      if (!this.campaign.templateGroupId || !this.selectedGroupId) return false;
      return this.campaign.templateGroupId !== this.selectedGroupId;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.FETCH_CREATIVE_TEMPLATE_PROPERTIES,
      contentTypes.FETCH_CREATIVE_TEMPLATES,
    ]),
    ...mapActions('templates', [
      templatesTypes.FETCH_TEMPLATE_GROUPS,
      templatesTypes.FETCH_TEMPLATES_IN_GROUP_ID,
      templatesTypes.SET_SELECTED_TEMPLATES,
      templatesTypes.CHANGE_SELECTED_CREATIVE_TEMPLATE_GROUP,
    ]),
    ...mapMutations('templates', [
      templatesTypes.UPDATE_AVAILABLE_TEMPLATES,
    ]),
    getTemplateProperties(templateId) {
      return this.FETCH_CREATIVE_TEMPLATE_PROPERTIES(templateId);
    },
    updateCreativeTemplateGroup() {
      this.emitSelectedTemplateGroup();
    },
    getTemplate(id) {
      return this.templateList
        .find((template) => template.id === id);
    },
    templateSelectionChanged() {
      this.$emit('change');
    },
    emitSelectedTemplateGroup() {
      if (this.selectedTemplateGroup === null) return;
      const isGroupSet = (typeof this.selectedTemplateGroup !== 'undefined')
        ? Object.keys(this.selectedTemplateGroup).length > 0 : false;
      this.groupSelected = isGroupSet;
      this.selectedGroupId = isGroupSet ? this.selectedTemplateGroup.id : null;
      if (this.selectedTemplateGroup?.id) {
        this.$emit('updateTemplateGroup', this.selectedTemplateGroup.id);
        this.toggleIsCreativeTemplateGroupChanged();
      }
    },
    toggleIsCreativeTemplateGroupChanged() {
      if (this.groupIdHasChanged) this.CHANGE_SELECTED_CREATIVE_TEMPLATE_GROUP(true);
      else this.CHANGE_SELECTED_CREATIVE_TEMPLATE_GROUP(false);
    },
    updateSelectTemplateGroup() {
      if (this.templateGroup === null || this.availableTemplateGroups.length === 0) return;
      this.selectedTemplateGroup = this.availableTemplateGroups
        .find((group) => group.id === this.templateGroup);
      this.emitSelectedTemplateGroup();
    },
    getTemplateGroupId(groups) {
      return !this.GET_IS_MANAGED_CAMPAIGN ? groups.find(((group) => group.name === 'Self Service'))?.id : this.campaign.templateGroupId;
    },
    populateCreativeTemplateList(templates) {
      this.templateList = templates;
      this.setTemplateList(templates);
      const selectedTemplatesIds = [];
      if (this.GET_IS_NEW_CAMPAIGN) {
        templates.forEach((template) => {
          selectedTemplatesIds.push(template.id);
        });
      } else {
        this.activeCreatives.forEach((creative) => {
          if (!creative.paused) selectedTemplatesIds.push(creative.creativeTemplateId);
        });
      }
      this.SET_SELECTED_TEMPLATES(selectedTemplatesIds);
    },
    fetchTemplateGroups() {
      this.FETCH_TEMPLATE_GROUPS()
        .then((groups) => {
          this.availableTemplateGroups = groups;
          this.getCreativeTemplates();
        });
    },
    getCreativeTemplates() {
      if (this.GET_IS_MANAGED_CAMPAIGN && this.GET_IS_NEW_CAMPAIGN) return;
      const payload = {
        templateGroupId: this.getTemplateGroupId(this.availableTemplateGroups),
        campaignId: this.campaign.id,
      };
      if (this.GET_IS_MANAGED_CAMPAIGN || this.GET_IS_NEW_CAMPAIGN) delete payload.campaignId;
      else if (!this.GET_IS_CAMPAIGN_STATE('DRAFT')) delete payload.templateGroupId;
      this.FETCH_CREATIVE_TEMPLATES(payload).then((templates) => { this.populateCreativeTemplateList(templates); });
    },
    getTemplatesByGroupId(id) {
      this.FETCH_TEMPLATES_IN_GROUP_ID(id)
        .then((templates) => {
          this.templateList = templates;
          this.setTemplateList(templates);
        });
    },
    updateIsValidTemplateSelection() {
      this.$emit('isValidTemplateSelection', this.selectedTemplateGroup?.id
          || this.isValidSelfServiceTemplateSelection);
    },
    setIsValidSelfServiceTemplateSelection(isValid) {
      this.isValidSelfServiceTemplateSelection = isValid;
      this.updateIsValidTemplateSelection();
    },
    setTemplateList(templateList) {
      return new Promise(async (resolve, reject) => {
        try {
          const templates = templateList.map(({ id }) => this.getTemplateProperties(id));
          const list = await Promise.all(templates).then((values) => values);
          this.UPDATE_AVAILABLE_TEMPLATES(list);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },
  },
  async created() {
    this.updateIsValidTemplateSelection();
    await this.fetchTemplateGroups();
    await this.emitSelectedTemplateGroup();
  },
  mounted() {
    this.updateSelectTemplateGroup();
  },
  watch: {
    campaign() {
      this.updateSelectTemplateGroup();
    },
    selectedTemplateGroup(val) {
      if (val?.id) {
        this.getTemplatesByGroupId(val.id);
      }
    },
    templateGroup() {
      this.updateSelectTemplateGroup();
      this.updateIsValidTemplateSelection();
    },
    availableTemplateGroups() {
      this.updateSelectTemplateGroup();
    },
    selectedTemplates() {
      this.updateIsValidTemplateSelection();
    },
  },
};
</script>
<style lang="scss" scoped>
</style>
